import React, { useState } from 'react';
import { UsersIcon, TrendingUpIcon, CalendarClockIcon, KeyIcon, SunIcon, SparklesIcon, AlertTriangleIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { ResourceCalendar } from './loisirs/ResourceCalendar';
import { ACTIVITIES, CAMPS, ACCESS_POINTS } from '../data/loisirs';
import { RESOURCES } from '../data/windsor';

/** Bookable venues, ranked by occupancy for the performance panel. */
const FACILITIES = RESOURCES.filter(r => r.kind === 'plateau' && r.bookable).map(r => ({
  id: r.id,
  name: r.name,
  capacity: r.capacity ?? 0,
  rateCitizen: r.rateCitizen ?? 0,
  rateOrganization: r.rateOrganization ?? 0,
  occupancy: r.occupancy ?? 0
})).sort((a, b) => b.occupancy - a.occupancy);

/** Weekly attendance at the aquatic complex, used for the access chart. */
const ACCESS_WEEK = [{
  day: {
    fr: 'Lun',
    en: 'Mon'
  },
  entries: 388,
  denied: 12
}, {
  day: {
    fr: 'Mar',
    en: 'Tue'
  },
  entries: 402,
  denied: 9
}, {
  day: {
    fr: 'Mer',
    en: 'Wed'
  },
  entries: 451,
  denied: 15
}, {
  day: {
    fr: 'Jeu',
    en: 'Thu'
  },
  entries: 418,
  denied: 11
}, {
  day: {
    fr: 'Ven',
    en: 'Fri'
  },
  entries: 496,
  denied: 21
}, {
  day: {
    fr: 'Sam',
    en: 'Sat'
  },
  entries: 612,
  denied: 28
}, {
  day: {
    fr: 'Dim',
    en: 'Sun'
  },
  entries: 534,
  denied: 17
}];
function MetricCard({
  icon: Icon,
  label,
  value,
  hint,
  tone = 'muted'
}: {
  icon: typeof UsersIcon;
  label: string;
  value: string;
  hint?: string;
  tone?: 'muted' | 'positive' | 'warning';
}) {
  return <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-100" data-id="element-3314" data-mp="1skbltsjr6zep">
      <div className="flex items-center gap-3 mb-2" data-id="element-3315" data-mp="1nrigb2ggnn1k">
        <div className="p-2 bg-indigo-50 text-[#4F46E5] rounded-lg" data-id="element-3316" data-mp="1ityspbxvhxwy">
          <Icon className="w-5 h-5" data-id="element-3317" data-mp="1irmen9v8k7p6" />
        </div>
        <h3 className="text-sm font-medium text-slate-600" data-id="element-3318" data-mp="2g9cmuuq9b89y">{label}</h3>
      </div>
      <div className="flex items-end gap-3 flex-wrap" data-id="element-3319" data-mp="2binwjiqbchce">
        <span className="text-3xl font-bold text-[#0F172A] font-heading" data-id="element-3320" data-mp="2bh34bzrh1ahp">
          {value}
        </span>
        {hint && <span className={`text-sm mb-1 ${tone === 'positive' ? 'font-medium text-emerald-600' : tone === 'warning' ? 'font-medium text-amber-600' : 'text-slate-500'}`} data-id="element-3321" data-mp="2zdpwg44zgefo">
            {hint}
          </span>}
      </div>
    </div>;
}
export function LoisirsAnalytics() {
  const {
    t,
    lang
  } = useLanguage();
  const locale = lang === 'fr' ? 'fr-CA' : 'en-CA';
  const [selectedFacility, setSelectedFacility] = useState(FACILITIES[0].id);
  const totalRegistered = ACTIVITIES.reduce((s, a) => s + a.registered, 0);
  const totalCapacity = ACTIVITIES.reduce((s, a) => s + a.capacity, 0);
  const totalWaitlist = ACTIVITIES.reduce((s, a) => s + a.waitlisted, 0);
  const campRegistered = CAMPS.reduce((s, c) => s + c.registered, 0);
  const healthMissing = CAMPS.reduce((s, c) => s + (c.registered - c.healthFormsComplete), 0);
  const maxEntries = Math.max(...ACCESS_WEEK.map(d => d.entries));
  const facility = FACILITIES.find(f => f.id === selectedFacility) ?? FACILITIES[0];
  const revenue = ACTIVITIES.reduce((s, a) => s + a.registered * a.priceResident, 0);
  const money = (value: number) => value.toLocaleString(locale, {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0
  });
  return <div className="space-y-8 animate-in fade-in duration-300" data-id="element-3322" data-mp="295tjjwty7yf5">
      {/* KPIs */}
      <section data-id="element-3323" data-mp="244ck85r44k0o">
        <h2 className="text-lg font-bold text-[#0F172A] font-heading mb-4 flex items-center gap-2" data-id="element-3324" data-mp="246qh8k5cs6av">
          <TrendingUpIcon className="w-5 h-5 text-[#4F46E5]" data-id="element-3325" data-mp="2i2169gwx1x7b" />
          {t('Vue d’ensemble de la saison', 'Season overview')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-3326" data-mp="npkr1qm578qb">
          <MetricCard icon={UsersIcon} label={t('Inscriptions aux activités', 'Activity registrations')} value={totalRegistered.toLocaleString(locale)} hint={`${Math.round(totalRegistered / totalCapacity * 100)} % ${t('des places', 'of spots')}`} tone="positive" data-id="element-3327" data-mp="irj7l4kt17eb" />
          <MetricCard icon={SunIcon} label={t('Enfants en camp de jour', 'Children in day camp')} value={campRegistered.toLocaleString(locale)} hint={`${healthMissing} ${t('fiches santé à venir', 'health forms pending')}`} tone="warning" data-id="element-3328" data-mp="u7xorzpkvouf" />
          <MetricCard icon={CalendarClockIcon} label={t('Occupation des plateaux', 'Facility occupancy')} value={`${Math.round(FACILITIES.reduce((s, f) => s + f.occupancy, 0) / FACILITIES.length)} %`} hint={t('moyenne hebdomadaire', 'weekly average')} data-id="element-3329" data-mp="6hl7hy8g0fr6" />
          <MetricCard icon={KeyIcon} label={t('Entrées cette semaine', 'Entries this week')} value={ACCESS_WEEK.reduce((s, d) => s + d.entries, 0).toLocaleString(locale)} hint={`${ACCESS_WEEK.reduce((s, d) => s + d.denied, 0)} ${t('refus', 'denied')}`} tone="warning" data-id="element-3330" data-mp="3jvycol8r5h4a" />
        </div>
      </section>

      {/* Activity performance */}
      <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-100" data-id="element-3331" data-mp="2xfxz85fmnija">
        <h3 className="text-lg font-bold text-[#0F172A] font-heading mb-1" data-id="element-3332" data-mp="2te31tc0thpas">
          {t('Remplissage par activité', 'Fill rate by activity')}
        </h3>
        <p className="text-sm text-slate-500 mb-6" data-id="element-3333" data-mp="2okrpwwh5eic0">
          {t('Les activités complètes avec liste d’attente signalent une demande non servie.', 'Full activities with a waitlist signal unmet demand.')}
        </p>
        <div className="overflow-x-auto" data-id="element-3334" data-mp="29ptxy4pwvuvg">
          <table className="w-full text-sm text-left" data-id="element-3335" data-mp="29rojnl90br0l">
            <thead className="text-xs text-slate-500 uppercase bg-slate-50" data-id="element-3336" data-mp="25nqk59frse70">
              <tr data-id="element-3337" data-mp="1v34yu3zknjpl">
                <th className="px-4 py-3 rounded-l-lg" data-id="element-3338" data-mp="1v2lq9dvta7o8">
                  {t('Activité', 'Activity')}
                </th>
                <th className="px-4 py-3" data-id="element-3339" data-mp="203j97sonb5qs">{t('Type', 'Type')}</th>
                <th className="px-4 py-3" data-id="element-3340" data-mp="1m2qfha664eoq">{t('Remplissage', 'Fill rate')}</th>
                <th className="px-4 py-3" data-id="element-3341" data-mp="1r1a1tboobshm">{t('Attente', 'Waitlist')}</th>
                <th className="px-4 py-3 rounded-r-lg" data-id="element-3342" data-mp="1r3e11gbmo3bk">
                  {t('Revenus', 'Revenue')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-3343" data-mp="2iregctpr5lax">
              {ACTIVITIES.map(activity => {
              const rate = Math.round(activity.registered / activity.capacity * 100);
              return <tr key={activity.id} data-id="element-3344" data-mp="2xpqdt2ky8y35">
                    <td className="px-4 py-3 font-medium text-slate-900" data-id="element-3345" data-mp="2xop00qcjtxhq">
                      {activity.name[lang]}
                    </td>
                    <td className="px-4 py-3 text-slate-600" data-id="element-3346" data-mp="32ijf1gh7loah">
                      {activity.type[lang]}
                    </td>
                    <td className="px-4 py-3" data-id="element-3347" data-mp="37u6s3ris674u">
                      <div className="flex items-center gap-2" data-id="element-3348" data-mp="37t5djd2k32id">
                        <span className="w-10 tabular-nums" data-id="element-3349" data-mp="3llblwczgw579">{rate} %</span>
                        <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-3350" data-mp="3gm8pfgxjz7aw">
                          <div className={`h-full ${rate >= 100 ? 'bg-red-500' : 'bg-[#4F46E5]'}`} style={{
                        width: `${Math.min(rate, 100)}%`
                      }} data-id="element-3351" data-mp="2sweghagcu2vx" />
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-3352" data-mp="2xu661xtj1cwy">
                      {activity.waitlisted > 0 ? <span className="inline-flex items-center gap-1 text-amber-700 font-medium" data-id="element-3353" data-mp="i2ic8b9t80id">
                          <AlertTriangleIcon className="w-3.5 h-3.5" data-id="element-3354" data-mp="d7cpja8l2ht8" />
                          {activity.waitlisted}
                        </span> : <span className="text-slate-400" data-id="element-3355" data-mp="2bnv95kqbprau">—</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-700 font-medium" data-id="element-3356" data-mp="2gku4g6lq44am">
                      {money(activity.registered * activity.priceResident)}
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Access attendance */}
      <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-100" data-id="element-3357" data-mp="2vcvwx5lgrv0m">
        <h3 className="text-lg font-bold text-[#0F172A] font-heading mb-1" data-id="element-3358" data-mp="2zajvqjqa27xs">
          {t('Achalandage du complexe aquatique', 'Aquatic complex attendance')}
        </h3>
        <p className="text-sm text-slate-500 mb-6" data-id="element-3359" data-mp="14zx17cmyc47s">
          {t('Entrées validées par le contrôle d’accès, sept derniers jours.', 'Entries validated by access control, last seven days.')}
        </p>
        <div className="flex items-end justify-between gap-3 h-48" data-id="element-3360" data-mp="12g0i6e4izuq2">
          {ACCESS_WEEK.map(day => <div key={day.day.fr} className="flex-1 flex flex-col items-center gap-2 h-full justify-end" data-id="element-3361" data-mp="sodydte4gnbp">
              <span className="text-xs font-bold text-[#0F172A] tabular-nums" data-id="element-3362" data-mp="nqm3cqi6kl3a">
                {day.entries}
              </span>
              <div className="w-full bg-[#4F46E5] rounded-t-md transition-all hover:bg-[#4338CA]" style={{
            height: `${day.entries / maxEntries * 100}%`
          }} role="img" aria-label={`${day.day[lang]} : ${day.entries} ${t('entrées', 'entries')}`} data-id="element-3363" data-mp="1laywrdt0lzor" />
              <span className="text-xs font-medium text-slate-500" data-id="element-3364" data-mp="3w4urnmdo6ppn">
                {day.day[lang]}
              </span>
            </div>)}
        </div>
      </section>

      <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-100" data-id="element-3365" data-mp="3s5vpg9jbp71w">
        <ResourceCalendar data-id="element-3366" data-mp="2jjmfvlj9itxj" />
      </section>

      {/* Facility detail */}
      <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-100" data-id="element-3367" data-mp="2fj2eg5rnp45l">
        <h3 className="text-lg font-bold text-[#0F172A] font-heading mb-4" data-id="element-3368" data-mp="2ta7jjndsmonb">
          {t('Rendement des plateaux', 'Facility performance')}
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6" data-id="element-3369" data-mp="2ogmi1lczrcjd">
          <ul className="space-y-1.5" data-id="element-3370" data-mp="335ioxf3bajie">
            {FACILITIES.map(f => {
            const active = f.id === selectedFacility;
            return <li key={f.id} data-id="element-3371" data-mp="3c8a2bkfqzpco">
                  <button onClick={() => setSelectedFacility(f.id)} className={`w-full text-left px-3.5 py-2.5 rounded-lg text-sm transition-colors ${active ? 'bg-[#0F172A] text-white' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-3372" data-mp="3c7i8of5po1p1">
                    <span className="block font-medium truncate" data-id="element-3373" data-mp="3ojav948zstid">
                      {f.name[lang]}
                    </span>
                    <span className={`block text-xs ${active ? 'text-slate-300' : 'text-slate-400'}`} data-id="element-3374" data-mp="3tgkoyw3ezrc0">
                      {f.occupancy} % {t('occupé', 'occupied')}
                    </span>
                  </button>
                </li>;
          })}
          </ul>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 content-start" data-id="element-3375" data-mp="3aquwwgr1djns">
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200" data-id="element-3376" data-mp="3aorz3ajn6m8i">
              <p className="text-xs text-slate-500" data-id="element-3377" data-mp="2wsoapxvg983y">
                {t('Occupation', 'Occupancy')}
              </p>
              <p className="text-2xl font-bold text-[#0F172A] font-heading" data-id="element-3378" data-mp="30urmyopy0gqa">
                {facility.occupancy} %
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200" data-id="element-3379" data-mp="z1lit5foyqw9">
              <p className="text-xs text-slate-500" data-id="element-3380" data-mp="27ys59ru6drm1">{t('Capacité', 'Capacity')}</p>
              <p className="text-2xl font-bold text-[#0F172A] font-heading" data-id="element-3381" data-mp="22z5s1ieckbd8">
                {facility.capacity}
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200" data-id="element-3382" data-mp="1yydj6q9e08hh">
              <p className="text-xs text-slate-500" data-id="element-3383" data-mp="1tz963ojcdqlv">
                {t('Tarif citoyen', 'Citizen rate')}
              </p>
              <p className="text-2xl font-bold text-[#0F172A] font-heading" data-id="element-3384" data-mp="2qo8j1guvfgke">
                {money(facility.rateCitizen)}
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200" data-id="element-3385" data-mp="2hvwsluzrbt4f">
              <p className="text-xs text-slate-500" data-id="element-3386" data-mp="2cxd4pp1ls79f">
                {t('Tarif organisme', 'Organization rate')}
              </p>
              <p className="text-2xl font-bold text-[#0F172A] font-heading" data-id="element-3387" data-mp="3aczcx31uisz7">
                {facility.rateOrganization === 0 ? t('Gratuit', 'Free') : money(facility.rateOrganization)}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ATNA recommendations */}
      <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-100" data-id="element-3388" data-mp="3hwihcya97p0t">
        <h3 className="text-lg font-bold text-[#0F172A] font-heading mb-4 flex items-center gap-2" data-id="element-3389" data-mp="3mts240mp7cib">
          <SparklesIcon className="w-5 h-5 text-[#4F46E5]" data-id="element-3390" data-mp="3qyhyrrp6kv4a" />
          {t('Recommandations ATNA', 'ATNA recommendations')}
        </h3>
        <ul className="space-y-3" data-id="element-3391" data-mp="3vu6xxtxaga02">
          <li className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50/60 px-4 py-3" data-id="element-3392" data-mp="4oyjhtikipiu">
            <AlertTriangleIcon className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" data-id="element-3393" data-mp="9hi0l0ggs2se" />
            <p className="text-sm text-slate-700" data-id="element-3394" data-mp="eftafb6redam">
              {t(`${totalWaitlist} enfants sont en liste d’attente en natation et céramique — ouvrir un second groupe couvrirait la demande.`, `${totalWaitlist} children are waitlisted in swimming and ceramics — opening a second group would cover demand.`)}
            </p>
          </li>
          <li className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50/60 px-4 py-3" data-id="element-3395" data-mp="5sfip23vxj3r">
            <AlertTriangleIcon className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" data-id="element-3396" data-mp="jjkcvoohhj6p" />
            <p className="text-sm text-slate-700" data-id="element-3397" data-mp="ejplzl4pktln">
              {t(`${healthMissing} fiches santé manquent à trois semaines du début des camps — une relance automatisée est recommandée.`, `${healthMissing} health forms are missing three weeks before camps begin — an automated reminder is recommended.`)}
            </p>
          </li>
          <li className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3" data-id="element-3398" data-mp="3rzdobp3yk4gw">
            <SparklesIcon className="w-4 h-4 text-[#4F46E5] mt-0.5 flex-shrink-0" data-id="element-3399" data-mp="1c7gffa0qi5be" />
            <p className="text-sm text-slate-700" data-id="element-3400" data-mp="178vdn65u3dku">
              {t('Le samedi concentre 18 % des entrées au complexe aquatique : ajouter un créneau de bain libre le dimanche matin lisserait l’achalandage.', 'Saturday accounts for 18% of aquatic complex entries: adding a Sunday morning open swim would smooth attendance.')}
            </p>
          </li>
          <li className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3" data-id="element-3401" data-mp="2l4mz5onu2rxh">
            <SparklesIcon className="w-4 h-4 text-[#4F46E5] mt-0.5 flex-shrink-0" data-id="element-3402" data-mp="38ur3xzcrcxq2" />
            <p className="text-sm text-slate-700" data-id="element-3403" data-mp="3cw2uc73ghmzb">
              {t(`Les inscriptions aux activités représentent ${money(revenue)} pour la saison, dont 62 % provenant des programmes aquatiques.`, `Activity registrations represent ${money(revenue)} for the season, 62% of it from aquatic programs.`)}
            </p>
          </li>
          <li className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3" data-id="element-3404" data-mp="3rra2xvtul58w">
            <SparklesIcon className="w-4 h-4 text-[#4F46E5] mt-0.5 flex-shrink-0" data-id="element-3405" data-mp="gs4gnqkuu7t" />
            <p className="text-sm text-slate-700" data-id="element-3406" data-mp="a9wfpp91frs7">
              {t(`${ACCESS_POINTS.filter(p => !p.online).length} lecteur d’accès est hors ligne : les entrées y sont comptées manuellement, ce qui fausse l’achalandage de l’aréna.`, `${ACCESS_POINTS.filter(p => !p.online).length} access reader is offline: entries there are counted manually, skewing arena attendance.`)}
            </p>
          </li>
        </ul>
      </section>
    </div>;
}